import React, { useCallback, useEffect, useRef, useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { BookOpen, Loader2, ImagePlus, Send, X as XIcon } from "lucide-react";

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

/**
 * Community Board — visitors and locals contribute historical photos and
 * stories about Popoyo. Approved items show below the curated history timeline.
 */
export default function CommunityBoard() {
  const { user } = useAuth();
  const { open: openAuth } = useAuthDialog();

  // form state
  const [title, setTitle] = useState("");
  const [story, setStory] = useState("");
  const [year, setYear] = useState("");
  const [location, setLocation] = useState("");
  const [contributor, setContributor] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFileId, setPhotoFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  // approved list
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/community/submissions", { params: { limit: 60 } });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      /* silent — public feed */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setTitle(""); setStory(""); setYear(""); setLocation(""); setContributor("");
    setPhotoPreview(null); setPhotoFileId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(f.type)) {
      toast.error("Please choose a JPG, PNG, WEBP or GIF image.");
      return;
    }
    if (f.size > MAX_PHOTO_BYTES) {
      toast.error("Image is too large (5 MB max).");
      return;
    }
    setUploading(true);
    try {
      const previewUrl = URL.createObjectURL(f);
      setPhotoPreview(previewUrl);
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await http.post("/community/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoFileId(data.file_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
      setPhotoPreview(null);
      setPhotoFileId(null);
      if (fileRef.current) fileRef.current.value = "";
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setPhotoFileId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user?.id) { openAuth("login"); return; }
    if (title.trim().length < 3 || story.trim().length < 10) {
      toast("Add a short title and at least a sentence or two of story.");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/community/submissions", {
        title: title.trim(),
        story: story.trim(),
        year: year.trim() || null,
        location: location.trim() || null,
        contributor_name: contributor.trim() || null,
        photo_file_id: photoFileId || null,
      });
      toast.success("Thanks — your story is queued for review. Pura vida!");
      resetForm();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="community-board"
      data-testid="community-board-section"
      className="clay p-6 md:p-7 scroll-mt-24 mt-8"
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: "var(--navy)" }}
        >
          <BookOpen className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="eyebrow text-[color:var(--coral)]">Community history board</p>
          <h2 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>
            Share a piece of Popoyo history
          </h2>
          <p className="text-sm text-[color:var(--ink-2)] mt-1 max-w-2xl">
            Old family photo of the bay? A story from before the boom? Send it in. Our team reviews every submission — approved stories appear here for the whole community.
          </p>
        </div>
      </div>

      {/* Submission form */}
      {user?.id ? (
        <form onSubmit={submit} className="space-y-2" data-testid="community-submission-form">
          <div className="grid sm:grid-cols-[1fr_140px_180px] gap-2">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (e.g. Popoyo Surf Lodge opening, 1999)"
              maxLength={140}
              className="rounded-2xl"
              data-testid="community-title"
              required
            />
            <Input
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="Year (e.g. 1968)"
              maxLength={16}
              className="rounded-2xl"
              data-testid="community-year"
            />
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Location (Playa Popoyo…)"
              maxLength={140}
              className="rounded-2xl"
              data-testid="community-location"
            />
          </div>
          <Textarea
            value={story}
            onChange={(e) => setStory(e.target.value)}
            placeholder="Your story — what was happening in this photo / memory? (10-4000 characters)"
            rows={4}
            maxLength={4000}
            className="rounded-2xl"
            data-testid="community-story"
            required
          />
          <Input
            value={contributor}
            onChange={(e) => setContributor(e.target.value)}
            placeholder={`Contributor name shown publicly (default: ${user.name || user.username || "your name"})`}
            maxLength={80}
            className="rounded-2xl"
            data-testid="community-contributor"
          />

          {/* Photo picker */}
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={onPickPhoto}
              className="hidden"
              data-testid="community-photo-input"
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              variant="outline"
              disabled={uploading}
              className="pill"
              data-testid="community-photo-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1.5" />}
              {photoFileId ? "Photo added" : "Add a photo (optional)"}
            </Button>
            {photoPreview && (
              <div className="relative inline-block">
                <img
                  src={photoPreview}
                  alt="preview"
                  className="w-16 h-16 rounded-2xl object-cover border border-[color:var(--ink-2)]/20"
                />
                <button
                  type="button"
                  onClick={removePhoto}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[color:var(--emergency)] text-white flex items-center justify-center"
                  aria-label="Remove photo"
                  data-testid="community-photo-remove"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>
            )}
            <span className="text-[11px] text-[color:var(--ink-2)]">JPG · PNG · WEBP · 5 MB max</span>
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
            <span className="text-[11px] text-[color:var(--ink-2)]">
              Signed in as <strong>{user.name || user.username}</strong> · your submission goes to our review queue
            </span>
            <Button
              type="submit"
              disabled={submitting || uploading}
              data-testid="community-submit"
              className="pill bg-[color:var(--coral)] text-white hover:opacity-90"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Submit story
            </Button>
          </div>
        </form>
      ) : (
        <div
          className="rounded-2xl bg-[color:var(--sand-2)] p-4 text-sm text-[color:var(--ink-2)] flex items-center justify-between gap-3 flex-wrap"
          data-testid="community-login-prompt"
        >
          <span>Log in to share a photo or story with the community.</span>
          <Button
            onClick={() => openAuth("login")}
            className="pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]"
            data-testid="community-login-btn"
          >
            Log in
          </Button>
        </div>
      )}

      {/* Approved feed */}
      <div className="mt-6">
        <p className="eyebrow mb-3 text-[color:var(--coral)]">From the community</p>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading community stories…
          </div>
        ) : items.length === 0 ? (
          <p
            className="text-sm text-[color:var(--ink-2)] italic"
            data-testid="community-feed-empty"
          >
            No community stories yet. Be the first to share one above.
          </p>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="community-feed">
            {items.map((it) => (
              <li
                key={it.id}
                className="rounded-2xl border border-[color:var(--ink-2)]/10 bg-white overflow-hidden flex flex-col"
                data-testid={`community-item-${it.id}`}
              >
                {it.photo_file_id && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/community/photos/${it.photo_file_id}`}
                    alt={it.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-48 object-cover bg-[color:var(--sand-2)]"
                  />
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-[11px] uppercase tracking-widest text-[color:var(--coral)]">
                    {[it.year, it.location].filter(Boolean).join(" · ") || "Community story"}
                  </p>
                  <h3 className="text-lg font-bold mt-1" style={{ fontFamily: "Outfit" }}>{it.title}</h3>
                  <p className="text-sm text-[color:var(--ink)] mt-2 whitespace-pre-wrap flex-1">
                    {it.story}
                  </p>
                  <p className="text-[11px] text-[color:var(--ink-2)] mt-3">
                    — {it.contributor_name || "Anonymous"}
                    {it.approved_at ? ` · ${new Date(it.approved_at).toLocaleDateString()}` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
